import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  HttpError,
  type HttpClient,
  type RequestConfig,
} from '../models/httpClient.models';

export type Middleware = {
  request?: (
    config: InternalAxiosRequestConfig<unknown>
  ) =>
    | Promise<InternalAxiosRequestConfig<unknown>>
    | InternalAxiosRequestConfig<unknown>;
  response?: (
    res: AxiosResponse<unknown, unknown>
  ) =>
    | Promise<AxiosResponse<unknown, unknown>>
    | AxiosResponse<unknown, unknown>;
  errorHandler?: (
    error: AxiosError<unknown, unknown>
  ) => boolean | void | Promise<boolean | void>;
};

export type HttpClientOptions = {
  baseURL?: string;
  middleware?: Middleware;
};

// HTTP/2 responses carry no reason phrase, so statusText can be '' (e.g. a
// bodyless 401). Path only (no host/query) keeps fallback messages stable so
// Sentry groups occurrences of the same endpoint together.
const toUrlPath = (url = ''): string => {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url;
  }
};

export class AxiosHttpClient implements HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(options?: HttpClientOptions) {
    const { baseURL, middleware } = options || {};

    this.axiosInstance = axios.create({ baseURL });

    if (middleware) {
      this.axiosInstance.interceptors.request.use((config) =>
        middleware.request ? middleware.request(config) : config
      );
      this.axiosInstance.interceptors.response.use((response) =>
        middleware.response ? middleware.response(response) : response
      );
    }

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error instanceof AxiosError) {
          // A truthy result means the error was handled upstream (e.g. an auth
          // redirect is in flight), so it must not also escape as an HttpError.
          // Awaited so async handlers signal handled/unhandled via their
          // resolved value rather than the (always truthy) promise itself.
          if (await middleware?.errorHandler?.(error)) {
            return new Promise(() => undefined);
          }

          if (error.response) {
            const { status, statusText } = error.response;
            const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
            throw new HttpError(
              statusText ||
                `HTTP ${status} ${method} ${toUrlPath(error.config?.url)}`,
              status,
              {
                data: error.response.data,
                request: error.response.request,
                headers: error.response.headers,
              }
            );
          }
        } else if (error instanceof HttpError) {
          throw error;
        }
        throw new HttpError(
          error.message || 'Unknown HTTP client error',
          0,
          error
        );
      }
    );
  }
  async get<R>(url: string, config?: RequestConfig) {
    const response = await this.axiosInstance.get<R>(url, config);
    return this.handleResponse(response);
  }

  async post<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.post<R>(url, data, config);
    return this.handleResponse(response);
  }

  async put<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.put<R>(url, data, config);
    return this.handleResponse(response);
  }

  async patch<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.patch<R>(url, data, config);
    return this.handleResponse(response);
  }

  async delete(url: string, config?: RequestConfig): Promise<void> {
    const response = await this.axiosInstance.delete(url, config);
    return this.handleResponse(response);
  }

  private async handleResponse<R>(response: AxiosResponse<R>) {
    if (response.status < 300) {
      return response.data;
    }
    throw new HttpError(response.statusText, response.status, {
      data: response.data,
      request: response.request,
      headers: response.headers,
    });
  }
}
